import { inject, signal, WritableSignal } from '@angular/core';
import { getState, patchState, signalStoreFeature, type, watchState, withHooks, withMethods, withProps } from '@ngrx/signals';
import { DEFAULT_STATE_STORE, IndexedDbStorageService } from '../storage/indexed-db.service';

export interface IndexedDbSyncOptions<State extends object> {
  /** Unique key under which this state slice is stored in IndexedDB */
  key: string;
  /** Name of the object store (defaults to 'gars_app_state') */
  storeName?: string;
  /** State keys that should NOT be persisted to IndexedDB (e.g. loading, error, transient flags) */
  excludeKeys?: (keyof State)[];
  /** Debounce delay in ms before persisting state (default: 150ms) */
  debounceMs?: number;
  /** Optional callback fired when hydration from IndexedDB is complete */
  onHydrated?: (state: Partial<State>) => void;
}

export function withIndexedDbSync<State extends object>(options: IndexedDbSyncOptions<State>) {
  const {
    key,
    storeName = DEFAULT_STATE_STORE,
    excludeKeys = [],
    debounceMs = 150,
    onHydrated
  } = options;

  const isHydratedSignal: WritableSignal<boolean> = signal(false);

  return signalStoreFeature(
    { state: type<State>() },
    withProps(() => ({
      isHydrated: isHydratedSignal.asReadonly()
    })),
    withMethods((store, storage = inject(IndexedDbStorageService)) => ({
      /**
       * Manually clear this store's persisted data from IndexedDB.
       */
      async clearPersistedState(): Promise<void> {
        await storage.delete(key, storeName);
      },
      /**
       * Re-read and re-apply state from IndexedDB into the signal store.
       */
      async reloadFromIndexedDb(): Promise<void> {
        const saved = await storage.get<Partial<State>>(key, storeName);
        if (saved && typeof saved === 'object') {
          patchState(store, saved as any);
        }
      }
    })),
    withHooks({
      onInit(store, storage = inject(IndexedDbStorageService)) {
        let debounceTimer: ReturnType<typeof setTimeout> | null = null;
        let isInitialLoad = true;
        let pendingSaveDuringInit = false;

        const persistState = (state: State) => {
          if (debounceTimer) {
            clearTimeout(debounceTimer);
          }

          debounceTimer = setTimeout(async () => {
            const rawState = getState(store) as Record<string, unknown>;
            const stateToSave: Record<string, unknown> = {};

            for (const [k, v] of Object.entries(rawState)) {
              if (!excludeKeys.includes(k as keyof State)) {
                stateToSave[k] = v;
              }
            }

            try {
              await storage.set(key, stateToSave, storeName);
            } catch (err) {
              console.warn(`[withIndexedDbSync] Error persisting state to IndexedDB for key "${key}":`, err);
            }
          }, debounceMs);
        };

        // 1. Asynchronously hydrate store from IndexedDB
        storage.get<Partial<State>>(key, storeName).then(saved => {
          if (saved && typeof saved === 'object') {
            try {
              // Deep-merge saved records with any in-memory state modified during initial startup
              const currentState = getState(store) as Record<string, unknown>;
              const merged: Record<string, unknown> = { ...saved };

              for (const [stateKey, currentVal] of Object.entries(currentState)) {
                const savedVal = (saved as Record<string, unknown>)[stateKey];
                if (
                  currentVal &&
                  typeof currentVal === 'object' &&
                  !Array.isArray(currentVal) &&
                  savedVal &&
                  typeof savedVal === 'object' &&
                  !Array.isArray(savedVal)
                ) {
                  merged[stateKey] = {
                    ...(savedVal as Record<string, unknown>),
                    ...(currentVal as Record<string, unknown>)
                  };
                } else if (currentVal !== undefined && savedVal === undefined) {
                  merged[stateKey] = currentVal;
                }
              }

              patchState(store, merged as any);
              if (onHydrated) {
                onHydrated(merged as Partial<State>);
              }
            } catch (err) {
              console.warn(`[withIndexedDbSync] Failed to patch state for key "${key}":`, err);
            }
          }
          isHydratedSignal.set(true);
          isInitialLoad = false;

          // If changes occurred during initial load, persist current state to IndexedDB
          if (pendingSaveDuringInit) {
            persistState(getState(store));
          }
        }).catch(err => {
          console.warn(`[withIndexedDbSync] Failed to hydrate key "${key}" from IndexedDB:`, err);
          isHydratedSignal.set(true);
          isInitialLoad = false;
          if (pendingSaveDuringInit) {
            persistState(getState(store));
          }
        });

        // 2. Watch state changes and persist to IndexedDB
        watchState(store, (state: State) => {
          if (isInitialLoad) {
            pendingSaveDuringInit = true;
            return;
          }
          persistState(state);
        });
      }
    })
  );
}
